import qs from 'qs';
import { CARRIER_PICKUPS } from '../../constants/endpoints';
import { PICKUP } from '../../constants/returnMethods';
import { buildPickupAddressFields, buildPickupFieldsFromFormat } from '../../lib/addresses';
import { NAME_TO_FIRST_LAST_NAME } from '../../constants/addressFields';
import { receiveShippingAddress } from '../addresses';

// Scheduled Pickup
export const RECEIVE_CARRIER_PICKUP_SUBMIT = 'RECEIVE_CARRIER_PICKUP_SUBMIT';
export const RECEIVE_FETCHING_CARRIER_PICKUP_DATA = 'RECEIVE_FETCHING_CARRIER_PICKUP_DATA';
export const RECEIVE_CARRIER_PICKUP_DATA_ERROR = 'RECEIVE_CARRIER_PICKUP_DATA_ERROR';
export const RECEIVE_CARRIER_PICKUP_DATA = 'RECEIVE_CARRIER_PICKUP_DATA';
export const UPDATE_CARRIER_PICKUP_ADDRESS = 'UPDATE_CARRIER_PICKUP_ADDRESS';
export const UPDATE_CARRIER_PICKUP_DATE = 'UPDATE_CARRIER_PICKUP_DATE';
export const UPDATE_CARRIER_PICKUP_TIME_WINDOW = 'UPDATE_CARRIER_PICKUP_TIME_WINDOW';
export const UPDATE_CARRIER_PICKUP_SHOW_FORM = 'UPDATE_CARRIER_PICKUP_SHOW_FORM';
export const UPDATE_CARRIER_PICKUP_FORM_FIELD = 'UPDATE_CARRIER_PICKUP_FORM_FIELD';
export const UPDATE_CARRIER_PICKUP_CANCEL_FORM = 'UPDATE_CARRIER_PICKUP_CANCEL_FORM';
export const UPDATE_CARRIER_PICKUP_FIELDS_MISSING = 'UPDATE_CARRIER_PICKUP_FIELDS_MISSING';

export const receiveCarrierPickupSubmit = (pickupInfo) => ({
  type: RECEIVE_CARRIER_PICKUP_SUBMIT,
  data: {
    pickupInfo,
  },
});

export const fetchCarrierPickupData = (selectedOptionMethodKey) => async (dispatch, getState) => {
  const {
    page: { browserFingerprint, locale },
    retailerSettings: { name: retailerName } = {},
    orderInfo: { currentOrderNumber },
    returnOptions: {
      returnOptionMethods: {
        [selectedOptionMethodKey]: { carrier, get_address: getAddress } = {},
      } = {},
    },
    returnItems: { returnItems = [] },
    carrierPickup: { carrier: prevCarrier } = {},
  } = getState();

  if (prevCarrier === carrier) return;

  try {
    dispatch({
      type: RECEIVE_FETCHING_CARRIER_PICKUP_DATA,
      data: {
        isFetching: true,
      },
    });

    const requestData = {
      order_number: currentOrderNumber,
      return_method: PICKUP,
      return_carrier: carrier,
      return_items: returnItems,
    };

    const response = await fetch(
      CARRIER_PICKUPS({
        retailerName,
        carrier,
        params: qs.stringify({ locale }),
      }),
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'narvar-tfid': browserFingerprint,
        },
        body: JSON.stringify(requestData),
      },
    );
    const apiData = await response.json();

    const {
      country,
      pickup_dates: pickupDates = [],
      address_form_format: addressFormFormat,
      pickup_dates_required: pickupDatesRequired = true,
    } = apiData;
    const layout =
      buildPickupFieldsFromFormat(addressFormFormat) ||
      buildPickupAddressFields({ country, replaceFields: NAME_TO_FIRST_LAST_NAME });

    const partiallyComplete = layout.some((field) => {
      if (!field.required) return false;
      return !apiData[field.key];
    });

    const address = layout.reduce(
      (acc, field) => ({
        ...acc,
        [field.key]: apiData[field.key],
      }),
      {},
    );

    await dispatch({
      type: RECEIVE_CARRIER_PICKUP_DATA,
      data: {
        carrier,
        isFetching: false,
        pickupForm: {
          pickupDates,
          showPickupDates: pickupDatesRequired || pickupDates.length > 0,
          address,
          form: {
            show: Boolean(getAddress && partiallyComplete),
            cancellable: !partiallyComplete,
            address:
              layout.find((field) => field.key === 'country') && address.country
                ? {
                    country: address.country,
                  }
                : {},
            layout,
            incompleteFields: [],
          },
        },
      },
    });
  } catch (error) {
    dispatch({
      type: RECEIVE_FETCHING_CARRIER_PICKUP_DATA,
      data: {
        isFetching: false,
      },
    });
    dispatch({
      type: RECEIVE_CARRIER_PICKUP_DATA_ERROR,
    });
  }
};

export const updateCarrierPickupAddress = (address) => ({
  type: UPDATE_CARRIER_PICKUP_ADDRESS,
  data: {
    pickupForm: {
      address,
    },
  },
});

export const updateCarrierPickupDate = (pickupDate, pickupTimeZone, pickupDates = []) => ({
  type: UPDATE_CARRIER_PICKUP_DATE,
  data: {
    pickupForm: {
      pickupDate,
      pickupTimeZone,
      pickupTimeWindows: pickupDates.find(
        ({ pickup_date: _pickupDate }) => pickupDate === _pickupDate,
      )?.pickup_time_slots,
    },
  },
});

export const updateCarrierPickupTimeWindow = (pickupTimeWindow) => ({
  type: UPDATE_CARRIER_PICKUP_TIME_WINDOW,
  data: {
    pickupForm: {
      pickupTimeWindow,
    },
  },
});

export const setShowForm = (show) => ({
  type: UPDATE_CARRIER_PICKUP_SHOW_FORM,
  data: {
    pickupForm: {
      form: {
        show,
      },
    },
  },
});

export const updateFormField = ({ key, value }) => ({
  type: UPDATE_CARRIER_PICKUP_FORM_FIELD,
  data: {
    key,
    value,
  },
});

export const receiveIncompleteFields = (incompleteFields) => ({
  type: UPDATE_CARRIER_PICKUP_FIELDS_MISSING,
  data: {
    pickupForm: {
      form: {
        incompleteFields,
      },
    },
  },
});

export const saveForm = () => (dispatch, getState) => {
  const { carrierPickup: { form: { address, layout } = {} } = {} } = getState();
  const incompleteFields = layout.reduce((acc, { required, key, pattern }) => {
    const filledValue = address[key];
    const requiredFieldIsBlank = required ? !filledValue : false;
    const fieldDoesNotMatchPattern =
      filledValue && pattern ? filledValue.match(pattern) === null : false;

    if (requiredFieldIsBlank || fieldDoesNotMatchPattern) {
      return [...acc, key];
    }
    return acc;
  }, []);

  if (incompleteFields.length > 0) {
    return dispatch(receiveIncompleteFields(incompleteFields));
  }
  dispatch(updateCarrierPickupAddress(address));
  return dispatch(receiveShippingAddress(address));
};

export const cancelForm = () => ({
  type: UPDATE_CARRIER_PICKUP_CANCEL_FORM,
});
